import { get, set, del } from 'idb-keyval';
import { createElement } from 'react';

import { App, AppMachine } from './app/AppState';
import { AudioDirector } from './audio/AudioDirector';
import { Category as AudioCategory } from './audio/VoicePool';
import { SECONDARY_LOOP, weaponSound } from './audio/weaponSounds';
import { Fam } from './sim/pools/BulletPool';
import balance from './content/balance.json';
import { buildIndex } from './content/registry';
import {
  CAMPAIGN_START,
  hpScaleOf,
  isLastLevelOfWorld,
  levelAt,
  levelCountOf,
  levelNumberOf,
  nextSpot,
  WORLD_COUNT,
  WORLDS,
  worldNumberOf,
  type CampaignSpot,
} from './content/campaign';
import { Director } from './game/Director';
import { FrameStats } from './platform/FrameStats';
import { InputSource } from './platform/InputSource';
import { Loop } from './platform/Loop';
import { probeCapabilities, suggestPreset } from './platform/Capabilities';
import { ContextLossGuard } from './platform/ContextLoss';
import { requestPersistence, SaveStore, type KeyValueStore } from './platform/Storage';
import { acquire, rollOffer } from './sim/cards';
import {
  CAP_ENEMIES,
  CAP_ENEMY_BULLETS,
  CAP_PICKUPS,
  CAP_PLAYER_BULLETS,
  DT,
  PF_H,
  PF_HW,
  ULTI_CHARGE_SEC,
} from './sim/constants';
import type { Balance } from './sim/defs';
import { Ev } from './sim/events';
import type { SecondaryDef } from './sim/secondary';
import { checkFeelInvariants } from './sim/FeelConstants';
import type { ModDef } from './sim/mods';
import { cos, sin, TAU } from './sim/math';
import { dps, Stat, type Modifier } from './sim/stats';
import { syncStats, xpRatio } from './sim/systems/progressionSystem';
import { endlessStage } from './sim/systems/endlessSystem';
import {
  creditsForRun,
  maxConstraints,
  metaBonuses,
  metaModifiers,
  newlyEarned,
  type RunRecord,
} from './game/meta';
import { BriefingScreen } from './ui/briefing';
import {
  loadoutModifiers,
  resolveSecondary,
  resolveShip,
  resolveWeapon,
  unlockedSecondaries,
  unlockedShips,
  unlockedWeapons,
} from './game/loadout';
import {
  constraintBonuses,
  constraintMask,
  cumulUnlocked,
  resolveConstraints,
  unlockedConstraints,
  type ConstraintDef,
} from './content/constraints';
import { BOMBS, resolveBomb, unlockedBombs } from './content/bombs';
import { clampDifficulty, DIFFICULTY_NAMES, type DifficultyId } from './sim/difficulty';
import { DevScreen } from './ui/dev';
import { BestiaryScreen } from './ui/bestiary';
import { MetaScreen } from './ui/meta';
import { TrainingScreen } from './ui/training';
import { drillsFor, trainingUnlocked, type Exercice } from './game/training';
import { OptionsScreen } from './ui/options';
import type { SummaryData } from './ui/screens';
import { step } from './sim/step';
import { emptyInput, World, type InputFrame } from './sim/World';
import { attractInput, ATTRACT_TICKS } from './content/attract';
import { armAttract, ATTRACT_SHIP_ID } from './game/attract';
import { Onboarding } from './game/Onboarding';
import { CoachOverlay } from './ui/coach';
import { ExplosionFx } from './view/fx/ExplosionFx';
import { TrailRibbons } from './view/fx/TrailRibbons';
import { BlackHoleFx } from './view/fx/BlackHoleFx';
import { SmokeFx } from './view/fx/SmokeFx';
import { QualityDirector } from './view/QualityDirector';
import { Stage } from './view/Stage';
import { VfxPipeline } from './view/VfxPipeline';
import { View } from './view/View';
import { Warmup } from './view/Warmup';
import { worldGrade } from './view/WorldGrade';
import { Hud } from './ui/hud/Hud';
import { UiRoot } from './ui/mount';
import { installUiSounds } from './ui/uiSfx';
import { CardsScreen, PauseScreen, SummaryScreen, TitleScreen } from './ui/screens';
import './ui/styles.css';

/* ═══════════════════════════════════════════════════════════════════════════
   Point d'entrée. Il orchestre, il ne décide de rien.

   La règle qui structure ce fichier : la SIMULATION ne tourne que dans l'état
   PLAY, et l'arbre React n'existe que hors de PLAY. Les deux ne coexistent
   jamais — c'est ce qui garantit qu'aucun `setState` ne peut se produire
   pendant les 16,67 ms qui comptent (PLAN.md §3.7).
   ═══════════════════════════════════════════════════════════════════════════ */

const idbStore: KeyValueStore = {
  get: (k) => get(k),
  set: (k, v) => set(k, v),
  del: (k) => del(k),
};

const stage = new Stage();
const defs = buildIndex(balance as Balance);
const world = new World(0x1337, defs);
const view = new View(stage, CAP_ENEMY_BULLETS, CAP_PLAYER_BULLETS, CAP_ENEMIES, CAP_PICKUPS);
const input = new InputSource();
const director = new Director();
const explosions = new ExplosionFx(stage.scene, stage.renderer, 4096);
// Traînée de propulsion du vaisseau (couche rubans, J5). Dans la scène
// principale → bloomée, et sous la couche danger.
const trails = new TrailRibbons();
stage.scene.add(trails.mesh);
// Fumée du canon en surchauffe : de la vapeur qui monte quand ça chauffe, et un
// crachat franc à l'enrayage. Dans la scène principale (bloomée), sous les tirs.
const smoke = new SmokeFx(stage.scene);
// Vortex de charge de l'ulti — mise en scène bespoke des armes LASER.
const blackHole = new BlackHoleFx();
stage.scene.add(blackHole.mesh);
/** Secondaires dont l'ulti se charge dans un TROU NOIR (le canon orbital). Les
 *  autres armes gardent la charge générique (lumière + bloom). */
const BLACK_HOLE_SECS = new Set(['rayon-continu', 'faisceau-bref']);
const audio = new AudioDirector(matchMedia('(pointer: coarse)').matches);

/* ⚠️ ORDRE OBLIGATOIRE : sonder, décider le preset, PUIS construire le
   pipeline. `lowTier` n'existe qu'au constructeur — il détermine le nombre de
   passes de bloom et la présence de SMAA, qui ne se rétractent pas ensuite.
   Décider après aurait imposé de reconstruire le pipeline, donc un à-coup
   visible, au moment précis où l'on cherche à en éviter un. */
const caps = probeCapabilities(stage.renderer);
const preset = suggestPreset(caps);
const vfx = new VfxPipeline(stage.renderer, stage.scene, stage.overlay, stage.camera, {
  lowTier: preset === 'bas',
});
const quality = new QualityDirector();
const warmup = new Warmup();
const workStats = new FrameStats(240);
const paceStats = new FrameStats(240);
const saves = new SaveStore(idbStore);

const app = new AppMachine();
const ui = new UiRoot();
const hud = new Hud();
// Sons d'interface par délégation : une fois posés, tous les menus (présents et
// à venir) sonnent sans branchement. `audio.play` est silencieux tant que le
// contexte n'est pas débloqué, donc poser l'écouteur au boot est sans risque.
installUiSounds(ui.container, (id) => audio.play(id, AudioCategory.Ui, 0, 1));

// Le pointeur est attaché au CANVAS, pas à la fenêtre : les écrans React
// vivent au-dessus et doivent recevoir leurs propres clics sans que le jeu les
// intercepte.
input.attach(window, stage.renderer.domElement);
/* Les deux zones tactiles (bombe, arme secondaire) ne sont montées que si le
   pointeur est grossier : sur desktop, ni noeud ni écouteur. Elles écrivent
   dans les mêmes champs `bomb` / `secondary` que le clavier — un second chemin
   d'entrée finirait par diverger du premier. */
input.attachTouchButtons(document.body);

/** Demi-étendue du cadrage caméra, en unités monde. C'est le SEUL endroit du
 *  projet qui connaisse ce calcul — les deux conversions ci-dessous doivent
 *  rester exactement inverses l'une de l'autre. */
function halfExtent(): { w: number; h: number } {
  const h = stage.camera.position.z * Math.tan((30 * Math.PI) / 360);
  return { w: h * stage.camera.aspect, h };
}

/** Position normalisée du pointeur → unités monde. */
function aimFromPointer(): { x: number; y: number } | null {
  if (!input.pointer.active) return null;
  const e = halfExtent();
  return { x: input.pointer.x * e.w, y: input.pointer.y * e.h };
}

/** Unités monde → position normalisée. Sert à recaler la cible tactile sur le
 *  vaisseau au démarrage d'une partie. */
function pointerFromWorld(x: number, y: number): { x: number; y: number } {
  const e = halfExtent();
  return { x: x / e.w, y: y / e.h };
}

/* ⚠️ Le composer a ses PROPRES render targets, indépendants de ceux du
   renderer : `Stage.resize()` ne les dimensionne pas.

   Régression vécue — cet appel avait disparu lors de la réécriture de `main.ts`
   au J8. `maxW`/`maxH` restaient à zéro, `VfxPipeline.render()` posait un
   viewport de taille nulle, et plus rien n'était dessiné : ni la scène, ni la
   couche overlay. Écran 3D entièrement noir, alors que la simulation, l'audio,
   le HUD et les écrans React fonctionnaient parfaitement — ce qui rend la
   panne très difficile à situer, puisque tout ce qu'on observe marche.

   Les cibles sont allouées à la taille MAXIMALE une fois, ici : le resolution
   scale ne bouge ensuite que le viewport, jamais leur taille (PLAN.md §4.9). */
function syncVfxSize(): void {
  vfx.setSize(window.innerWidth, window.innerHeight);
}
syncVfxSize();
window.addEventListener('resize', syncVfxSize);

/* ⚠️ L'entraînement est un drapeau de la couche APPLICATION, pas de la
   simulation. Le monde n'a aucune raison de savoir si la partie rapporte
   quelque chose : c'est `endRun` qui décide, et lui seul. Mettre ce drapeau
   dans `RunState` aurait fait entrer une notion de récompense dans src/sim/,
   qui n'en connaît aucune. */
let training = false;

/* Drapeaux d'application du mode SANS FIN, hors simulation pour la même raison
   que `training` : le monde n'a pas à connaître la notion de récompense.
   `endlessMode` dit que la RUN en cours est sans fin (pilote le bilan) ;
   `endlessRequested` dit que le prochain briefing lancera une partie sans fin
   (choisi à l'écran-titre, lu au démarrage). */
let endlessMode = false;
let endlessRequested = false;

/** Ennemis croisés pendant la session, pour le Journal de bord. Relevé côté
 *  application par échantillonnage : la simulation n'émet pas d'événement
 *  d'apparition, et lui en ajouter un pour alimenter un écran de menu serait
 *  payer un coût de tick pour une information qui n'a aucune urgence. */
const seenEnemies = new Set<string>();

let fxTime = 0;
let scrollOffset = 0;
/** Dernier `bombsUsed` observé, pour détecter le FRONT d'une bombe côté vue
 *  (onde de choc + gerbe) sans toucher à la simulation. */
let prevBombsUsed = 0;

/** Identité sonore de l'équipement courant : le son de tir primaire, et la
 *  boucle « en vol » de la secondaire (ou `null` si elle n'en a pas). Posés au
 *  démarrage d'une partie, lus par la boucle de rendu. */
let currentPrimarySound = 'player_shot';
let currentSecondaryLoop: string | null = null;

/** FX de l'ULTI, pilotés côté vue par le franchissement d'états sim (comme la
 *  bombe) : dernier `ultiShots` observé (front de décharge) et un pic de bloom. */
let prevUltiShots = 0;
let ultiBloomT = 0;
/** Pic de bloom qui retombe sur une BASCULE DE PHASE de boss (lu dans l'anneau
 *  d'événements — une phase vit par ennemi, sans front d'état simple). */
let bossFlashT = 0;
/** Accumulateur de cadence d'émission de FUMÉE : on lâche une bouffée à intervalle
 *  fixe tant que le canon est chaud, indépendamment du frame rate. */
let smokeEmitT = 0;

/* ── Musique adaptative ────────────────────────────────────────────────────
   Dernier niveau d'intensité poussé (pour n'appeler la musique qu'aux CHANGEMENTS
   de niveau, pas à chaque frame), et un drapeau d'HYSTÉRÉSIS : la batterie entre
   quand l'écran se remplit et ne repart que quand il se vide franchement — sans
   quoi elle clignoterait au passage d'un seuil. */
let lastMusicLevel = -1;
let combatHot = false;

/** Niveau d'intensité musical (0 à 3) déduit de l'état + de la pression à
 *  l'écran. 0 = silence (boot), 1 = calme (menus), 2 = jeu, 3 = combat dense. */
function targetMusicLevel(): number {
  switch (app.state) {
    case App.Play:
    case App.Attract: {
      if (app.state === App.Attract) return 2;
      if (world.run.bossSpawned) return 3;
      const n = world.enemies.count;
      if (combatHot) {
        if (n <= 6) combatHot = false;
      } else if (n >= 14) combatHot = true;
      return combatHot ? 3 : 2;
    }
    case App.Boot:
    case App.Loading:
      return 0;
    default:
      // Tous les menus, la pause et les cartes : la nappe respire, calme.
      return 1;
  }
}

/* ── Attract mode ────────────────────────────────────────────────────────
   Secondes d'inactivité sur l'écran-titre avant de lancer la démo, tick courant
   de la boucle de démo, et l'unique `InputFrame` recyclée qui porte ses entrées
   scriptées (aucune allocation par tick, comme le générateur du golden). */
const ATTRACT_IDLE_SEC = 12;
let idleSec = 0;
let attractTick = 0;
const attractFrame = emptyInput();

/* ── Onboarding ─────────────────────────────────────────────────────────────
   Le coach de première partie (glyphes sans mots) et sa machine à cinq temps.
   L'overlay est persistant ; `onboarding` n'est non-nul que pendant la toute
   première partie de campagne — sinon le coach reste masqué. */
const coach = new CoachOverlay();
let onboarding: Onboarding | null = null;

/** Offre de cartes courante et état du reroll. */
let offer: ModDef[] = [];
/* ⚠️ Un COMPTEUR, plus un booléen. Le nœud « Relance supplémentaire » (1 000
   crédits) accorde une seconde relance par offre : avec un booléen, il ne
   pouvait rien accorder du tout — c'était le dernier nœud fantôme de l'arbre,
   et le plus discret, puisque le bouton existait déjà et fonctionnait une fois. */
let rerollsLeft = 0;

director.onExplosion = (cls, x, y) => {
  explosions.burst(cls, x, y, fxTime, scrollOffset, () => world.rngVisual.next(), stage.lights);
  // Couche 3 : l'onde de choc, réservée aux explosions qui la demandent (le
  // popcorn ne la déclenche jamais — quinze morts/seconde rendraient l'écran
  // illisible). On mappe l'ampleur MONDE de l'onde vers des unités écran, bornée
  // pour qu'un boss ne retourne pas tout le cadre.
  if (cls.shockRadius > 0) {
    // Rayon et amplitude assez francs pour se LIRE : une onde minuscule sur une
    // scène sombre et clairsemée passe inaperçue. Bornés pour qu'un boss ne
    // retourne pas tout le cadre.
    const maxRadius = Math.min(1.4, 0.4 + cls.shockRadius / 300);
    const amplitude = Math.min(0.09, 0.03 + cls.shockRadius * 0.00007);
    vfx.shock(x, y, maxRadius, amplitude);
  }
};

/* ── Déblocage audio ──────────────────────────────────────────────────────
   ⚠️ Le geste utilisateur est obligatoire, et `unlock()` doit être RAPPELÉ
   tant qu'il échoue : un déblocage raté qui consomme son unique tentative
   laisse la page définitivement muette. */
function unlockAudio(): void {
  if (!audio.unlock()) return;
  applyOptions();
  window.removeEventListener('keydown', unlockAudio);
  window.removeEventListener('pointerdown', unlockAudio);
}
window.addEventListener('keydown', unlockAudio);
window.addEventListener('pointerdown', unlockAudio);

/* ── Rendu des écrans ─────────────────────────────────────────────────── */

/** Armes secondaires actuellement emportables. Recalculé à chaque appel : un
 *  achat au hangar doit se voir au briefing suivant sans redémarrer la page. */
/** Nœuds achetés, relus à chaque appel : un achat au hangar doit se voir au
 *  briefing suivant sans recharger la page. */
function achats(): Set<string> {
  return new Set(saves.data.unlocked);
}

function armesDisponibles(): SecondaryDef[] {
  return unlockedSecondaries(defs.metaNodes, new Set(saves.data.unlocked), defs.secondaries);
}

/** Contraintes de la partie en cours. Mémorisées ici parce que `endRun` en a
 *  besoin pour le calcul des crédits, et que la simulation n'en connaît qu'un
 *  masque de bits — pas les bonus économiques, qui ne la regardent pas. */
let contraintes: ConstraintDef[] = [];

/** Position courante dans la campagne (quel monde, quel niveau). Le repère
 *  unique qui pilote l'enchaînement des niveaux et des mondes. */
let spot: CampaignSpot = CAMPAIGN_START;

function startRun(
  difficulty: DifficultyId = clampDifficulty(saves.data.lastDifficulty),
  armeId: string = saves.data.secondaryId,
  drill: Exercice | null = null,
  endless = false,
  spotOverride: CampaignSpot | null = null,
  fromBlock = 0,
): void {
  training = drill !== null;
  endlessMode = endless;
  world.reset();

  const achetes = new Set(saves.data.unlocked);
  /* ⚠️ ORDRE FIXE : méta, puis vaisseau, puis arme. Le résultat mathématique
     est le même dans n'importe quel ordre, mais la multiplication flottante
     n'est pas associative — l'ordre change le dernier bit, donc l'empreinte de
     replay. Un jour où les scores seront revérifiés côté serveur, une
     permutation innocente rejetterait des parties légitimes. */
  const bonus = metaBonuses(defs.metaNodes, achetes);
  world.progress.baseMods.push(...metaModifiers(bonus));
  const ship = defs.ships[resolveShip(saves.data.shipId, unlockedShips(defs.metaNodes, achetes, defs.ships), defs.ships)];
  const weapon =
    defs.weapons[resolveWeapon(saves.data.weaponId, unlockedWeapons(defs.metaNodes, achetes, defs.weapons), defs.weapons)];
  world.progress.baseMods.push(...loadoutModifiers(ship, weapon));
  currentPrimarySound = weaponSound(weapon?.id ?? saves.data.weaponId);
  syncStats(world);

  // Les contraintes ne sont PAS disponibles à l'entraînement : elles paient en
  // crédits, et un entraînement n'en verse aucun. Les autoriser reviendrait à
  // proposer un marché dont un seul côté existe.
  contraintes = training
    ? []
    : resolveConstraints(saves.data.constraintIds, defs.metaNodes, achetes);
  /* ⚠️ APRÈS `reset()` et APRÈS `syncStats()`, jamais avant : `reset()`
     annulerait le palier, et `syncStats()` doit avoir tourné pour que les vies
     tiennent compte des bonus permanents. L'ordre est verrouillé par un test —
     l'inverser produirait une panne invisible, toutes les parties se jouant en
     Opérateur pendant que l'écran de briefing affiche autre chose. */
  const secIdx = resolveSecondary(armeId, armesDisponibles(), defs.secondaries);
  const secDef = defs.secondaries[secIdx];
  currentSecondaryLoop = secDef ? (SECONDARY_LOOP[secDef.id] ?? null) : null;
  world.armPlayer(difficulty, secIdx, {
    bomb: resolveBomb(saves.data.bombId, unlockedBombs(defs.metaNodes, achetes)),
    constraints: constraintMask(contraintes),
  });
  // Trois départs possibles. Le mode SANS FIN n'a ni niveau ni monde : c'est
  // `endlessSystem` qui peuple. Un exercice démarre sur son propre niveau, hors
  // campagne. Une partie normale part du premier niveau du premier monde — le
  // `hpScale` vient du MONDE.
  if (endless) {
    world.startEndless();
  } else if (drill) {
    world.startLevel(drill.levelId, hpScaleOf(drill.levelId), drill.fromBlock);
  } else {
    // `spotOverride` : la téléportation du menu développeur part d'un monde/niveau
    // quelconque, éventuellement à un bloc précis (`fromBlock`, pour sauter droit
    // au boss) ; une partie normale part du début de la campagne.
    spot = spotOverride ?? CAMPAIGN_START;
    world.startLevel(
      levelAt(spot) ?? 'monde-1',
      WORLDS[spot.worldIndex]!.hpScale,
      spotOverride ? fromBlock : 0,
    );
  }
  /* ⚠️ La cible visée est recalée sur la position de départ du vaisseau. Sans
     cela, le premier glissement d'une partie part du CENTRE de l'écran alors
     que le vaisseau apparaît en bas : il file vers le haut tout seul, avant
     même que le doigt n'ait bougé. Sans effet tant qu'un doigt pilote. */
  const aim0 = pointerFromWorld(world.player.x, world.player.y);
  input.syncAim(aim0.x, aim0.y);
  // La silhouette suit le vaisseau RÉELLEMENT équipé (résolu, déblocage compris).
  // `resolveShip` renvoie toujours un index valide ; le repli ne sert qu'au type.
  view.setShip(ship?.id ?? saves.data.shipId);
  director.reset();
  // Onboarding : le tout premier run de CAMPAGNE est le tutoriel. On l'exclut du
  // sans-fin, de l'entraînement et des téléportations dev (`spotOverride`) — et
  // `runsPlayed === 0` garantit qu'il ne reparaît jamais une fois une partie
  // achevée (le compteur monte en fin de run). Aucun champ de sauvegarde dédié.
  onboarding =
    !endless && drill === null && spotOverride === null && saves.data.runsPlayed === 0
      ? new Onboarding()
      : null;
  coach.hide();
  offer = [];
  rerollsLeft = rerollBudget();
  workStats.reset();
  paceStats.reset();
  app.to(App.Loading);
  app.to(App.Play);
  renderUi();
}

/* ── Attract mode ────────────────────────────────────────────────────────── */

/** (Re)pose le monde de démonstration et remet la boucle à zéro. Appelé au
 *  lancement de la démo ET à chaque bouclage (fin de démo ou mort du vaisseau
 *  scripté) : comme `armAttract` repart de la même graine, chaque boucle est
 *  rigoureusement identique — c'est là tout l'intérêt de déterminisme. */
function armAttractLoop(): void {
  armAttract(world, defs);
  view.setShip(ATTRACT_SHIP_ID);
  // Identité sonore du loadout de série de la démo (mêmes ids que `armAttract`).
  currentPrimarySound = weaponSound('vulcan');
  currentSecondaryLoop = SECONDARY_LOOP['lance-perforante'] ?? null;
  director.reset();
  attractTick = 0;
}

/** Lance l'attract mode depuis l'écran-titre : monte la démo, passe l'état à
 *  ATTRACT (simulation active + menu monté) et voile le menu à 25 %. */
function startAttract(): void {
  if (!app.to(App.Attract)) return;
  armAttractLoop();
  renderUi();
}

/** Quitte la démo au premier geste et rend un menu net et interactif. No-op si
 *  l'on n'est pas en attract mode — le rendre inconditionnel simplifie les
 *  écouteurs d'entrée qui l'appellent sans vérifier l'état. */
function stopAttract(): void {
  if (app.state !== App.Attract) return;
  app.to(App.Title);
  idleSec = 0;
  renderUi();
}

function endRun(victory: boolean): void {
  const pr = world.progress;
  const record: RunRecord = {
    worldsCleared: pr.worldsCleared,
    cleared: victory,
    bossesKilled: pr.bossesKilled,
    deaths: pr.deaths,
    bombsUsed: pr.bombsUsed,
    hitsTaken: pr.hitsTaken,
    kills: world.kills,
    graze: world.grazeCount,
    difficulty: world.run.difficulty,
    // La boutique de bord n'existe pas encore : aucune cellule n'est dépensée.
    cellsSpent: 0,
  };

  /* ⚠️ Un entraînement ne verse RIEN : ni crédits, ni jalons, ni record. C'est
     ce qui empêche de rejouer en boucle le boss du monde 3 — le plus lucratif
     du jeu — et de contourner par la porte de service l'anti-farm des quatre-
     vingt-dix secondes. Le calcul est court-circuité ici plutôt que dans
     `creditsForRun`, qui n'a aucune raison de connaître la notion. */
  const credits = training
    ? 0
    : creditsForRun(world.score, record, world.tick / 120, constraintBonuses(contraintes));

  /* ⚠️ Les JALONS sont évalués ici, une seule fois, et seuls les NOUVEAUX
     comptent. Un jalon déjà décroché ne redonne jamais de points : sinon
     rejouer le même niveau deviendrait la stratégie optimale, et les points de
     compétence — censés récompenser la MAÎTRISE — se transformeraient en
     récompense de temps passé. C'est précisément la dérive qui rendrait la
     troisième monnaie illisible. */
  const already = new Set(saves.data.milestones);
  const fresh = training ? [] : newlyEarned(defs.metaMilestones, record, already);

  // Record du PALIER joué, en plus du record global. Les deux servent à des
  // choses différentes : le global est un souvenir, celui du palier est un
  // objectif comparable.
  const d = clampDifficulty(world.run.difficulty);
  const bests = [...saves.data.bestByDifficulty];
  bests[d] = Math.max(bests[d] ?? 0, Math.floor(world.score));

  void saves.save(
    training
      ? // Seul le journal se remplit : c'est une observation, pas une récompense.
        { seenEnemies: [...seenEnemies] }
      : {
          credits: saves.data.credits + credits,
          bestScore: Math.max(saves.data.bestScore, Math.floor(world.score)),
          bestByDifficulty: bests,
          lastDifficulty: d,
          runsPlayed: saves.data.runsPlayed + 1,
          milestones: [...saves.data.milestones, ...fresh.map((m) => m.id)],
          seenEnemies: [...seenEnemies],
        },
  );

  lastSummary = {
    score: world.score,
    kills: world.kills,
    graze: world.grazeCount,
    level: pr.level,
    credits,
    // Le mode sans fin ne connaît pas la victoire : on ne le gagne pas, on le
    // survit. `victory` y reste faux quoi qu'il arrive.
    victory: endlessMode ? false : victory,
    world: worldNumberOf(spot),
    worldCount: WORLD_COUNT,
    // Bascule le bilan sur l'axe temps/palier. Absent en campagne.
    endless: endlessMode
      ? { seconds: world.run.t, stage: endlessStage(world.run.t) }
      : undefined,
    milestones: fresh.map((m) => ({ name: m.name, points: m.points })),
  };
  app.to(App.Summary);
  renderUi();
}

let lastSummary: SummaryData = {
  score: 0,
  kills: 0,
  graze: 0,
  level: 1,
  credits: 0,
  victory: false,
  world: 1,
  worldCount: WORLD_COUNT,
  milestones: [],
};

/** Avancement dans le niveau courant, pour la jauge du bas.
 *
 *  ⚠️ Lu depuis la DURÉE DÉCLARÉE du niveau et non d'une constante. La version
 *  précédente divisait par 130 en dur, ce qui était juste pour le monde 1 et
 *  aurait affiché une jauge pleine à mi-parcours des mondes suivants. */
function levelProgress(): number {
  const id = world.run.levelId;
  if (!id) return 0;
  const level = defs.levels.get(id);
  if (!level || level.duration <= 0) return 0;
  return Math.min(1, world.run.t / level.duration);
}

/**
 * Fin de monde : on enchaîne sur le suivant, ou la partie est gagnée.
 *
 * ⚠️ Les projectiles ennemis en vol sont EFFACÉS. Un rideau hérité du monde
 * précédent tuerait le joueur au premier instant du suivant, sur une menace
 * qu'il n'a aucune raison de surveiller — il vient de gagner. Les ramassables,
 * eux, survivent : le boss vient d'en lâcher quatorze, et ils lui sont dus.
 *
 * La carte offerte n'est pas une facilité. Franchir un monde doit produire
 * quelque chose d'immédiat, sinon la victoire se réduit à un changement de
 * décor — et le coefficient de PV du monde suivant, lui, s'applique tout de
 * suite.
 */
/**
 * Fin d'un niveau : on enchaîne dans la campagne à deux étages.
 *
 * ⚠️ `worldsCleared` ne s'incrémente QUE si le niveau terminé était le DERNIER
 * de son monde — c'est là qu'un MONDE est réellement franchi, et c'est ce que
 * lisent les jalons. Un niveau-étape franchi n'est pas un monde franchi.
 *
 * ⚠️ Le rideau de projectiles est effacé à chaque passage de niveau. Un tir
 * hérité du niveau précédent tuerait le joueur au premier instant du suivant,
 * sur une menace qu'il n'a aucune raison de surveiller — il vient de terminer
 * un palier.
 */
function advanceCampaign(): void {
  // On vient de terminer le DERNIER niveau du monde courant ? Alors on a franchi
  // un MONDE — c'est ce que lisent les jalons, et c'est ce qui donne une carte.
  const clearedWorld = isLastLevelOfWorld(spot);
  if (clearedWorld) world.progress.worldsCleared++;

  const next = nextSpot(spot);
  if (next === null) {
    endRun(true);
    return;
  }
  spot = next;
  world.eb.clear();
  world.startLevel(levelAt(spot) ?? 'monde-1', WORLDS[spot.worldIndex]!.hpScale);

  if (clearedWorld) {
    /* Franchir un MONDE est un événement : une carte offerte, comme le passage
       de monde l'a toujours fait. Le coefficient de PV du monde suivant, lui,
       s'applique déjà — la récompense compense l'aggravation. */
    // Fanfare de franchissement : le seul « on a passé un cap » du jeu.
    audio.play('world_clear', AudioCategory.Ui, 0, 1);
    world.progress.pendingLevelUps++;
    openCards();
  } else {
    /* Niveau-étape franchi À L'INTÉRIEUR d'un monde : la progression est
       CONTINUE. Pas de carte — en ajouter une par niveau gonflerait le build
       bien au-delà de la courbe de puissance visée, et le jeu deviendrait plus
       facile à mesure qu'on avance, l'exact inverse de l'intention. On signale
       le palier par une bannière et la partie reprend sans interruption. */
    hud.flashBanner(`MONDE ${worldNumberOf(spot)} · NIVEAU ${levelNumberOf(spot)}/${levelCountOf(spot.worldIndex)}`);
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   MENU DÉVELOPPEUR.

   Outils de test, PAS de jeu : téléportation à n'importe quel niveau, réglage du
   loadout, octroi d'améliorations en direct, triche méta. Rien ici ne respecte
   l'économie du jeu (crédits, jalons, courbe) — c'est le but. Le menu n'est offert
   que sous `import.meta.env.DEV` (voir l'écran de pause), donc absent d'un build
   de production.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Améliorations pré-accordées au lancement d'une téléportation. Permet de tester
 *  un boss « comme au niveau 15 » sans jouer les vingt minutes qui y mènent. */
let devPreLevels = 0;

/** Accorde `n` améliorations tirées au hasard à la run EN COURS, effet immédiat. */
function devGrantLevels(n: number): void {
  for (let i = 0; i < n; i++) {
    const offre = rollOffer(defs.mods, world.progress.owned, world.rng);
    if (offre.length > 0) acquire(world.progress.owned, offre[world.rng.int(offre.length)]!);
  }
  syncStats(world);
  renderUi();
}

/** Téléporte : démarre une run FRAÎCHE au niveau visé, avec le loadout sauvegardé
 *  courant et `devPreLevels` améliorations pré-accordées. */
function devJump(worldIndex: number, levelIndex: number): void {
  startRun(clampDifficulty(saves.data.lastDifficulty), saves.data.secondaryId, null, false, {
    worldIndex,
    levelIndex,
  });
  devApplyLive(); // loadout dev complet (contenu verrouillé + specs) sur la run fraîche
  if (devPreLevels > 0) devGrantLevels(devPreLevels);
}

/** Téléporte droit au COMBAT DE BOSS d'un monde (bloc `boss-` de son dernier
 *  niveau). Passe par `fromBlock` plutôt que par le chemin d'entraînement, pour
 *  rester une vraie run de campagne (la mise à mort fait avancer la campagne). */
function devJumpBoss(worldIndex: number): void {
  const w = WORLDS[worldIndex];
  if (!w) return;
  const levelIndex = w.levels.length - 1;
  const level = defs.levels.get(w.levels[levelIndex]!);
  const bloc = level ? level.blocks.findIndex((b) => b.wave.startsWith('boss-')) : -1;
  startRun(
    clampDifficulty(saves.data.lastDifficulty),
    saves.data.secondaryId,
    null,
    false,
    { worldIndex, levelIndex },
    bloc >= 0 ? bloc : 0,
  );
  devApplyLive();
  if (devPreLevels > 0) devGrantLevels(devPreLevels);
}

/** Relance le niveau COURANT à neuf, loadout et pré-niveaux compris. */
function devReplayHere(): void {
  startRun(clampDifficulty(saves.data.lastDifficulty), saves.data.secondaryId, null, false, spot);
  devApplyLive();
  if (devPreLevels > 0) devGrantLevels(devPreLevels);
}

/** Surcharges de specs dev, empilées à chaque clic et appliquées à chaud.
 *  Cumulables ; `devResetSpecs` les efface. */
const devOverrides: Modifier[] = [];

/** Le catalogue des retouches de specs proposées par le menu. Chaque clic empile
 *  une de ces valeurs. La recharge secondaire est un `mul` NÉGATIF (plus bas =
 *  mieux), comme partout dans le moteur. */
const DEV_SPECS: Readonly<Record<string, Modifier>> = {
  damage: { stat: Stat.Damage, op: 'mul', value: 0.25 },
  fireRate: { stat: Stat.FireRate, op: 'mul', value: 0.25 },
  bulletCount: { stat: Stat.BulletCount, op: 'add', value: 1 },
  bulletSpeed: { stat: Stat.BulletSpeed, op: 'mul', value: 0.2 },
  secCooldown: { stat: Stat.SecondaryCooldownMul, op: 'mul', value: -0.25 },
  secDamage: { stat: Stat.SecondaryDamageMul, op: 'mul', value: 0.25 },
};

/**
 * Reconstruit À CHAUD les modificateurs de base de la run depuis le loadout
 * sauvegardé + les surcharges de specs, et repose l'arme secondaire, la bombe et
 * le palier. C'est ce qui rend TOUT changement du menu dev instantané, sans
 * relancer la partie — les cartes ramassées en jeu (`progress.owned`) sont
 * conservées.
 *
 * ⚠️ Dev : on résout le contenu par IDENTIFIANT, SANS filtre de déblocage
 * (`shipByI`/`weaponByI`/`secondaryByI`), pour qu'un vaisseau ou une arme
 * verrouillée soit quand même jouable en test — contrairement au démarrage
 * normal qui, lui, retombe sur le contenu de série.
 */
function devApplyLive(): void {
  const achetes = new Set(saves.data.unlocked);
  const bonus = metaBonuses(defs.metaNodes, achetes);
  const ship = defs.ships[defs.shipByI.get(saves.data.shipId) ?? 0]!;
  const weapon = defs.weapons[defs.weaponByI.get(saves.data.weaponId) ?? 0]!;

  const base = world.progress.baseMods;
  base.length = 0;
  base.push(...metaModifiers(bonus), ...loadoutModifiers(ship, weapon), ...devOverrides);

  // L'identité SONORE suit le changement live : sans ça, on garde le son de
  // l'arme de départ et « tout sonne pareil » (bug remonté depuis le menu dev).
  currentPrimarySound = weaponSound(weapon.id);

  const secIdx = defs.secondaryByI.get(saves.data.secondaryId);
  if (secIdx !== undefined) {
    world.player.secondary = secIdx;
    world.player.secondaryCd = 0; // prête tout de suite après un changement
    const sd = defs.secondaries[secIdx];
    currentSecondaryLoop = sd ? (SECONDARY_LOOP[sd.id] ?? null) : null;
  }
  world.player.bomb = resolveBomb(saves.data.bombId, BOMBS);
  world.run.difficulty = clampDifficulty(saves.data.lastDifficulty);
  // Silhouette à jour aussi en direct : changer de vaisseau au menu dev le montre.
  view.setShip(ship.id);

  syncStats(world);
  renderUi();
}

/** Change une pièce du loadout sauvegardé ET l'applique à chaud (instantané). */
function devSetLoadout(patch: Partial<typeof saves.data>): void {
  void saves.save(patch);
  devApplyLive();
}

/** Empile une retouche de spec (dégâts, cadence, projectiles…) et l'applique. */
function devSpec(kind: string): void {
  const m = DEV_SPECS[kind];
  if (m) devOverrides.push({ ...m });
  devApplyLive();
}

/** Efface toutes les retouches de specs et réapplique le loadout nu. */
function devResetSpecs(): void {
  devOverrides.length = 0;
  devApplyLive();
}

/** Accorde une amélioration PRÉCISE à la run en cours (par identifiant). */
function devGrantMod(id: string): void {
  const mod = defs.mods.find((m) => m.id === id);
  if (mod) acquire(world.progress.owned, mod);
  syncStats(world);
  renderUi();
}

/** Vide toutes les améliorations de la run en cours (respec instantané). */
function devRespec(): void {
  world.progress.owned.length = 0;
  syncStats(world);
  renderUi();
}

/** Triche méta : crédits, tous les jalons (donc tous les points de compétence),
 *  tout l'arbre acheté. Pour éprouver les écrans de méta et les déblocages. */
function devCheat(kind: 'credits' | 'skill' | 'all'): void {
  if (kind === 'credits') {
    void saves.save({ credits: saves.data.credits + 5000 });
  } else if (kind === 'skill') {
    void saves.save({ milestones: defs.metaMilestones.map((m) => m.id) });
  } else {
    void saves.save({
      credits: 99999,
      milestones: defs.metaMilestones.map((m) => m.id),
      unlocked: defs.metaNodes.map((n) => n.id),
    });
  }
  renderUi();
}

/** Relances accordées à chaque offre de cartes.
 *
 *  Une de base, deux avec le nœud « Relance supplémentaire ». C'est un
 *  déblocage qui augmente la QUALITÉ des choix sans toucher à la puissance : il
 *  ne verse aucun chiffre, il rend seulement le build visé plus atteignable —
 *  raison pour laquelle il ne pèse pas sur le plafond de +35 %. */
function rerollBudget(): number {
  return saves.data.unlocked.includes('relance-supplementaire') ? 2 : 1;
}

function openCards(): void {
  offer = rollOffer(defs.mods, world.progress.owned, world.rng);
  rerollsLeft = rerollBudget();
  app.to(App.Cards);
  renderUi();
}

function pickCard(def: ModDef): void {
  acquire(world.progress.owned, def);
  // ⚠️ Sans cette recomposition, la carte est ramassée et n'a AUCUN effet :
  // `playerSystem` lit un cache qui n'a pas bougé.
  syncStats(world);
  world.progress.pendingLevelUps--;
  audio.play('pickup', AudioCategory.Ui, 0, 0.9);
  if (world.progress.pendingLevelUps > 0) openCards();
  else {
    app.to(App.Play);
    renderUi();
  }
}

function renderUi(): void {
  if (!app.reactMounted) {
    ui.unmount();
    hud.show();
    return;
  }
  hud.hide();
  // Voile de l'attract mode : le menu s'efface à 25 % pour laisser voir la démo.
  // Toggle inconditionnel — il éteint le voile dès qu'on quitte la démo.
  ui.setDimmed(app.state === App.Attract);

  switch (app.state) {
    // La démo affiche le MÊME écran-titre, simplement voilé et rendu inerte par
    // le CSS (`pointer-events: none`). Rien à câbler : les gestionnaires de
    // boutons ne sont jamais atteints tant que la démo tourne.
    case App.Attract:
    case App.Title:
      ui.render(
        createElement(TitleScreen, {
          best: saves.data.bestScore,
          onPlay: () => {
            endlessRequested = false;
            app.to(App.RunConfig);
            renderUi();
          },
          onEndless: () => {
            // Même briefing (difficulté, arme, contraintes) ; seul le drapeau
            // change ce que `onStart` lance ensuite.
            endlessRequested = true;
            app.to(App.RunConfig);
            renderUi();
          },
          onHangar: () => {
            app.to(App.Meta);
            renderUi();
          },
          onRegistre: () => {
            app.to(App.Journal);
            renderUi();
          },
          onOptions: () => {
            app.to(App.Options);
            renderUi();
          },
        }),
      );
      break;
    case App.Pause:
      ui.render(
        createElement(PauseScreen, {
          onResume: () => {
            app.to(App.Play);
            renderUi();
          },
          onOptions: () => {
            app.to(App.Options);
            renderUi();
          },
          onQuit: () => endRun(false),
          // Le menu développeur n'existe qu'en dev : en production, la prop est
          // absente et le bouton ne s'affiche pas.
          onDev: import.meta.env.DEV
            ? () => {
                app.to(App.Dev);
                renderUi();
              }
            : undefined,
        }),
      );
      break;
    case App.Dev:
      ui.render(
        createElement(DevScreen, {
          worlds: WORLDS,
          ships: defs.ships,
          weapons: defs.weapons,
          secondaries: defs.secondaries,
          bombs: BOMBS,
          mods: defs.mods,
          difficultyNames: DIFFICULTY_NAMES,
          current: {
            shipId: saves.data.shipId,
            weaponId: saves.data.weaponId,
            secondaryId: saves.data.secondaryId,
            bombId: saves.data.bombId,
            difficulty: clampDifficulty(saves.data.lastDifficulty),
            level: world.progress.level,
            credits: saves.data.credits,
          },
          specs: {
            damage: world.progress.stats.damage,
            fireRate: world.progress.stats.fireRate,
            bulletCount: world.progress.stats.bulletCount,
            bulletSpeed: world.progress.stats.bulletSpeed,
            dps: dps(world.progress.stats),
            secondaryCooldownMul: world.progress.stats.secondaryCooldownMul,
            secondaryDamageMul: world.progress.stats.secondaryDamageMul,
          },
          preLevels: devPreLevels,
          onPreLevels: (n) => {
            devPreLevels = n;
            renderUi();
          },
          onJump: (wi, li) => devJump(wi, li),
          onJumpBoss: (wi) => devJumpBoss(wi),
          onEndless: () => {
            startRun(clampDifficulty(saves.data.lastDifficulty), saves.data.secondaryId, null, true);
            devApplyLive();
            if (devPreLevels > 0) devGrantLevels(devPreLevels);
          },
          onReplay: () => devReplayHere(),
          onSetLoadout: (patch) => devSetLoadout(patch),
          onSpec: (kind) => devSpec(kind),
          onResetSpecs: () => devResetSpecs(),
          onGrantLevels: (n) => devGrantLevels(n),
          onGrantMod: (id) => devGrantMod(id),
          onRespec: () => devRespec(),
          onCheat: (kind) => devCheat(kind),
          onResume: () => {
            app.to(App.Play);
            renderUi();
          },
          onMenu: () => {
            app.to(App.Title);
            renderUi();
          },
        }),
      );
      break;
    case App.Cards:
      ui.render(
        createElement(CardsScreen, {
          offer,
          owned: world.progress.owned,
          level: world.progress.level,
          canReroll: rerollsLeft > 0,
          onReroll: () => {
            rerollsLeft--;
            offer = rollOffer(defs.mods, world.progress.owned, world.rng);
            renderUi();
          },
          onPick: pickCard,
        }),
      );
      break;
    case App.Summary:
      ui.render(
        createElement(SummaryScreen, {
          data: lastSummary,
          // Rejouer relance le MÊME mode : mourir en sans fin et rejouer ne doit
          // pas renvoyer en campagne. `endlessMode` porte le mode de la run
          // qu'on vient de terminer.
          onReplay: () =>
            startRun(clampDifficulty(saves.data.lastDifficulty), saves.data.secondaryId, null, endlessMode),
          onHangar: () => {
            // Après une run, on passe par le HANGAR plutôt que par le titre :
            // c'est là que les crédits et les points fraîchement gagnés
            // deviennent quelque chose. Renvoyer au menu ferait disparaître la
            // récompense sans que le joueur ait pu la regarder.
            app.to(App.Meta);
            renderUi();
          },
        }),
      );
      break;

    case App.RunConfig:
      ui.render(
        createElement(BriefingScreen, {
          initial: clampDifficulty(saves.data.lastDifficulty),
          armes: armesDisponibles(),
          armeInitiale: saves.data.secondaryId,
          vaisseaux: unlockedShips(defs.metaNodes, achats(), defs.ships),
          vaisseauInitial: saves.data.shipId,
          primaires: unlockedWeapons(defs.metaNodes, achats(), defs.weapons),
          primaireInitiale: saves.data.weaponId,
          bombes: unlockedBombs(defs.metaNodes, achats()),
          bombeInitiale: saves.data.bombId,
          contraintes: unlockedConstraints(defs.metaNodes, achats()),
          contraintesInitiales: saves.data.constraintIds,
          maxContraintes: maxConstraints(cumulUnlocked(defs.metaNodes, achats())),
          best: (d) => saves.data.bestByDifficulty[d] ?? 0,
          onStart: (c) => {
            void saves.save({
              lastDifficulty: c.difficulty,
              secondaryId: c.arme,
              shipId: c.vaisseau,
              weaponId: c.primaire,
              bombId: c.bombe,
              constraintIds: c.contraintes,
            });
            startRun(c.difficulty, c.arme, null, endlessRequested);
          },
          onBack: () => {
            endlessRequested = false;
            app.to(App.Title);
            renderUi();
          },
        }),
      );
      break;

    case App.Journal:
      ui.render(
        createElement(BestiaryScreen, {
          enemies: defs.enemies,
          rencontres: new Set(saves.data.seenEnemies),
          onBack: () => {
            app.to(App.Meta);
            renderUi();
          },
        }),
      );
      break;

    case App.Training:
      ui.render(
        createElement(TrainingScreen, {
          exercices: drillsFor(WORLDS, defs.levels, new Set(saves.data.unlocked), new Set(saves.data.milestones)),
          onStart: (e) => startRun(clampDifficulty(saves.data.lastDifficulty), saves.data.secondaryId, e),
          onBack: () => {
            app.to(App.Meta);
            renderUi();
          },
        }),
      );
      break;

    case App.Meta:
      ui.render(
        createElement(MetaScreen, {
          nodes: defs.metaNodes,
          milestones: defs.metaMilestones,
          state: {
            credits: saves.data.credits,
            milestones: saves.data.milestones,
            nodes: saves.data.unlocked,
          },
          // Chargement courant, pour l'aperçu 3D du hangar. Optionnel côté
          // MetaScreen (repli sur la coque de série s'il manque) — mêmes sources
          // que le briefing.
          loadout: {
            shipId: saves.data.shipId,
            weaponId: saves.data.weaponId,
            secondaryId: saves.data.secondaryId,
            upgrades: saves.data.unlocked,
            shipName: defs.ships.find((s) => s.id === saves.data.shipId)?.name,
            weaponName: defs.weapons.find((w) => w.id === saves.data.weaponId)?.name,
          },
          onChange: (next) => {
            void saves.save({
              credits: next.credits,
              unlocked: [...next.nodes],
              milestones: [...next.milestones],
            });
            renderUi();
          },
          onBack: () => {
            app.to(App.Title);
            renderUi();
          },
          onTraining: trainingUnlocked(new Set(saves.data.unlocked))
            ? () => {
                app.to(App.Training);
                renderUi();
              }
            : null,
        }),
      );
      break;

    case App.Options:
      ui.render(
        createElement(OptionsScreen, {
          data: saves.data.options,
          onChange: (next) => {
            void saves.save({ options: next });
            // ⚠️ Les réglages sont APPLIQUÉS immédiatement, pas au retour. Un
            // curseur de volume qu'il faut valider pour entendre l'effet ne
            // peut pas être réglé : on tâtonne à l'aveugle.
            applyOptions();
            renderUi();
          },
          onBack: () => {
            app.closeOptions();
            renderUi();
          },
        }),
      );
      break;

    default:
      ui.unmount();
      break;
  }
}

/** Reporte les réglages sauvegardés sur les systèmes qui les consomment.
 *
 *  Point de passage unique, appelé au démarrage ET à chaque modification. La
 *  panne qu'il évite est celle d'un réglage qui n'existe que dans le fichier de
 *  sauvegarde : lu, écrit, affiché, jamais transmis. */
function applyOptions(): void {
  const o = saves.data.options;
  audio.setVolume('master', o.master);
  audio.setVolume('music', o.music);
  audio.setVolume('sfx', o.sfx);
  director.toggles.shake = o.shake;
  director.toggles.flash = o.flash;
  // Accessibilité. `?? ` défend contre une sauvegarde d'avant ces réglages qui
  // aurait échappé à la fusion de `migrate()`.
  worldGrade.setColorblind(o.colorblind ?? 0);
  loop.setSpeed(o.gameSpeed ?? 1);
}

/* ── Boucle ──────────────────────────────────────────────────────────── */

const loop = new Loop({
  update: () => {
    // La simulation ne tourne que dans PLAY et ATTRACT. Tout écran est une PAUSE
    // DURE : le monde est figé, jamais ralenti (PLAN.md §3.7).
    if (!app.simRuns) return;

    const attract = app.state === App.Attract;
    if (attract && (attractTick >= ATTRACT_TICKS || world.player.lives <= 0)) {
      // Fin de la boucle de démo, ou vaisseau scripté perdu : on repose le monde
      // sur la même graine. Chaque boucle redevient identique au bit près — c'est
      // exactement ce que vérifie le test de déterminisme.
      armAttractLoop();
      return;
    }

    if (director.freezeTick(DT * 1000)) return;

    const wasFiring = world.player.fireCd;
    let frame: InputFrame;
    if (attract) {
      // On n'avance le tick de démo que lorsqu'un pas s'exécute VRAIMENT (le
      // freeze ci-dessus a pu en sauter un) : l'entrée scriptée reste ainsi
      // collée au tick de simulation, indexée tick pour tick.
      frame = attractInput((attractTick += 1), attractFrame);
    } else {
      frame = input.consume();
      const aim = aimFromPointer();
      frame.aimX = aim?.x;
      frame.aimY = aim?.y;
    }
    step(world, frame);
    if (world.player.fireCd > wasFiring) {
      director.onPlayerFired();
      audio.play(currentPrimarySound, AudioCategory.PlayerShot, world.player.x / PF_HW, 0.8);
      /* Bouche de canon : une lumière POOLÉE au point de tir. C'est le plus
         proche du « les balles éclairent » qu'autorise l'architecture — le
         plan interdit une lumière par projectile (ajouter une lumière recompile
         tous les shaders, risque n°4). Ici la même lampe pulse à la cadence de
         tir, et les ennemis bas la reçoivent.

         ⚠️ Priorité 0 : elle ne remplit qu'un slot LIBRE et ne vole jamais une
         lumière d'explosion. Une explosion prime toujours sur le glow d'un tir —
         c'est ce qui garantit que le muzzle flash n'affame pas le pool de trois
         lampes au moment où le boss explose. */
      stage.lights.flash(world.player.x, world.player.y + 20, 40, 0x8fe8ff, 4, 240, 0.06, 0);
    }
    director.ingest(world);
  },

  render: (alpha, frameMs) => {
    const t0 = performance.now();
    const dt = frameMs / 1000;
    fxTime += dt;
    if (app.simRuns) scrollOffset += 190 * dt;

    director.advance(frameMs);
    stage.lights.advance(dt);
    explosions.update(fxTime, scrollOffset);
    // Le bloom se pose plus bas, en UNE fois (base + pics d'ulti/boss cumulés).
    stage.setShake(director.shakeX(PF_H), director.shakeY(PF_H), director.shakeRoll());
    view.sync(world, alpha, director.recoil);

    // Traînée du vaisseau : suit sa queue, d'autant plus vive qu'il file. Mise à
    // jour AVANT `vfx.render` puisque le ruban vit dans la scène principale. Hors
    // partie, on la vide pour ne pas laisser un trait figé sous un menu.
    if (app.simRuns) {
      // Base permanente (le vaisseau file toujours vers l'avant dans le niveau) +
      // supplément à la vitesse latérale. `scrollOffset` ancre la dérive du jet.
      const p = world.player;
      const strength = Math.min(1, 0.35 + Math.hypot(p.vx, p.vy) / 700);
      trails.follow(p.x, p.y - 14, scrollOffset, strength);
    } else {
      trails.reset();
    }

    // FUMÉE du canon : de la vapeur qui monte en surchauffe (rouge vif), un crachat
    // plus dense à l'enrayage. Émise depuis la bouche du canon (au-dessus du
    // vaisseau), à cadence fixe. Rien tant que le canon n'est pas chaud.
    if (app.simRuns && (world.player.overheated || world.player.jammed)) {
      const jammed = world.player.jammed;
      const interval = jammed ? 0.035 : 0.075;
      smokeEmitT += dt;
      while (smokeEmitT >= interval) {
        smokeEmitT -= interval;
        smoke.emit(world.player.x, world.player.y + 26, jammed ? 1 : 0.5);
      }
    } else {
      smokeEmitT = 0;
    }
    smoke.update(dt);

    /* ── FX de l'ULTI ──────────────────────────────────────────────────────
       Pilotés par l'état sim (comme la bombe). CHARGE : une lumière qui enfle au
       vaisseau + un bloom qui monte (l'énergie s'amasse). DÉCHARGE (front de
       `ultiShots`) : onde de choc énorme + anneau clair + pic de bloom. */
    // Toutes les sources de PIC DE BLOOM se cumulent par max, puis se posent en
    // UNE fois avant le rendu : sinon le dernier `setBloomBoost` gagnerait et
    // effacerait les autres — un pic d'ulti annulé par une bascule de boss, par
    // exemple.
    let bloomBoost = director.bloomBoost;

    const up = world.player;
    if (app.simRuns && up.ultiChargeT > 0) {
      const prog = 1 - up.ultiChargeT / ULTI_CHARGE_SEC;
      const secId = defs.secondaries[up.secondary]?.id;
      if (secId !== undefined && BLACK_HOLE_SECS.has(secId)) {
        // Bespoke : le TROU NOIR du canon orbital, qui enfle et s'intensifie.
        blackHole.show(up.x, up.y, 110 + 150 * prog, prog, fxTime);
        bloomBoost = Math.max(bloomBoost, 1 + prog * 1.1);
      } else {
        blackHole.hide();
        stage.lights.flash(up.x, up.y, 50, 0x9fe8ff, 3 + 9 * prog, 200 + 200 * prog, 0.06, 6);
        bloomBoost = Math.max(bloomBoost, 1 + prog * 0.9);
      }
    } else {
      blackHole.hide();
    }
    if (world.player.ultiShots > prevUltiShots) {
      vfx.shock(up.x, up.y, 1.35, 0.1);
      explosions.bombBurst(up.x, up.y, fxTime, scrollOffset, () => world.rngVisual.next());
      ultiBloomT = 0.5;
    }
    prevUltiShots = world.player.ultiShots;
    if (ultiBloomT > 0) {
      ultiBloomT -= dt;
      bloomBoost = Math.max(bloomBoost, 1.7);
    }

    /* ── FX de BASCULE DE PHASE de boss ─────────────────────────────────────
       Lu dans l'anneau d'événements (une phase vit PAR ENNEMI, sans front d'état
       simple comme l'ulti). À chaque `BossPhase` : une onde de choc et un éclair
       chaud AU BOSS, plus un pic de bloom qui retombe — la montée en puissance se
       VOIT autant qu'elle s'entend. `BossKilled` fait une gerbe plus large. */
    if (app.simRuns) {
      const ring = world.events;
      for (let i = 0; i < ring.count; i++) {
        const type = ring.type(i);
        if (type === Ev.BossPhase) {
          vfx.shock(ring.b(i), ring.c(i), 1.25, 0.09);
          stage.lights.flash(ring.b(i), ring.c(i), 64, 0xffd27a, 9, 340, 0.05, 6);
          bossFlashT = 0.4;
        } else if (type === Ev.BossKilled) {
          vfx.shock(ring.b(i), ring.c(i), 1.5, 0.12);
          explosions.bombBurst(ring.b(i), ring.c(i), fxTime, scrollOffset, () =>
            world.rngVisual.next(),
          );
          bossFlashT = 0.6;
        }
      }
    }
    if (bossFlashT > 0) {
      bossFlashT -= dt;
      bloomBoost = Math.max(bloomBoost, 1.6);
    }

    vfx.setBloomBoost(bloomBoost);
    vfx.setScale(quality.sample(workStats.percentile(0.5), dt));
    vfx.render(dt);

    workStats.push(performance.now() - t0);
    paceStats.push(frameMs);

    audio.consume(world, PF_HW);

    // Musique adaptative : on ne pousse un niveau qu'à son CHANGEMENT. Gardé sur
    // `audio.ready` — la musique ne peut naître qu'après le déblocage — et c'est
    // aussi ce qui l'AMORCE : le premier frame débloqué voit un niveau ≠ -1 et
    // lance les couches via `setMusicIntensity`.
    if (audio.ready) {
      const lvl = targetMusicLevel();
      if (lvl !== lastMusicLevel) {
        audio.setMusicIntensity(lvl);
        lastMusicLevel = lvl;
      }
    }

    // Boucle « en vol » de la secondaire : le son tourne tant qu'un de ses
    // projectiles (famille PlayerSecondary) est à l'écran — le missile qui file,
    // le rayon qui dure, les scies qui tournent. `setSfxLoop` gère un seul son de
    // boucle à la fois (fondu d'entrée/sortie). Aucune boucle si l'arme n'en a
    // pas, ou hors partie.
    if (app.simRuns && currentSecondaryLoop) {
      const pb = world.pb;
      let inFlight = false;
      for (let i = pb.count - 1; i >= 0; i--) {
        if (pb.fam[pb.at(i)] === Fam.PlayerSecondary) {
          inFlight = true;
          break;
        }
      }
      audio.setSfxLoop(inFlight ? currentSecondaryLoop : null);
    } else {
      audio.setSfxLoop(null);
    }

    // La BOMBE, l'événement le plus visible du jeu : une onde de choc large et une
    // gerbe claire, au front de `bombsUsed`. La bombe ÉCLAT ne tue rien (elle
    // efface les balles) — sans ces effets propres, le joueur ne la « sent » pas.
    // `prevBombsUsed` se resynchronise à chaque frame, donc la remise à zéro d'un
    // début de run (bombsUsed → 0) ne déclenche jamais d'onde parasite.
    if (app.simRuns && world.progress.bombsUsed > prevBombsUsed) {
      vfx.shock(world.player.x, world.player.y, 1.05, 0.075);
      explosions.bombBurst(world.player.x, world.player.y, fxTime, scrollOffset, () =>
        world.rngVisual.next(),
      );
    }
    prevBombsUsed = world.progress.bombsUsed;

    if (app.state === App.Play) {
      // Échantillonnage du bestiaire : on relève ce qui est à l'écran. Aucune
      // urgence, aucun coût de tick — un `Set` de chaînes touché soixante fois
      // par seconde reste sous le bruit de mesure.
      for (let i = 0; i < world.enemies.count; i++) {
        const def = defs.enemies[world.enemies.kind[world.enemies.at(i)]!];
        if (def) seenEnemies.add(def.id);
      }
      hud.update(
        world,
        world.progress.level,
        xpRatio(world),
        levelProgress(),
      );
    }

    // Onboarding : la couche de coaching n'existe que pendant la première partie,
    // et seulement en jeu. `dt` est du temps réel — les plafonds de chaque temps
    // se mesurent en secondes murales, ce qui est le bon comportement pour un
    // filet anti-blocage. Toute autre situation (pause, menus, tutoriel fini)
    // retire le panneau.
    if (app.state === App.Play && onboarding && !onboarding.done) {
      onboarding.update(
        {
          playerX: world.player.x,
          playerY: world.player.y,
          grazeCount: world.grazeCount,
          bombsUsed: world.progress.bombsUsed,
          bulletCount: world.eb.count,
        },
        dt,
      );
      coach.show(onboarding.beat);
    } else {
      coach.hide();
    }

    debugPanel();
    world.events.clear();

    // Transitions pilotées par l'état du monde, évaluées APRÈS le rendu pour
    // que la dernière frame de jeu soit toujours affichée.
    if (app.state === App.Play) {
      if (world.progress.pendingLevelUps > 0) {
        // Carillon de montée de niveau — une récompense se lit à l'oreille. Ici,
        // pas dans `openCards()` : le franchissement de MONDE passe aussi par
        // `openCards`, mais il a sa propre fanfare (voir `advanceCampaign`).
        audio.play('levelup', AudioCategory.Ui, 0, 1);
        openCards();
      } else if (world.player.lives <= 0) endRun(false);
      else if (world.run.finished) {
        // Un entraînement joue un niveau isolé : il ne poursuit jamais la
        // campagne, il rend la main au bilan.
        if (training) endRun(true);
        else advanceCampaign();
      }
    }

    // Attract mode : au repos sur l'écran-titre, la démo se lance après le délai.
    // `dt` est du temps RÉEL (non affecté par la vitesse de jeu), donc le délai
    // se mesure en secondes murales, comme attendu d'un compte d'inactivité.
    if (app.state === App.Title) {
      idleSec += dt;
      if (idleSec >= ATTRACT_IDLE_SEC) startAttract();
    } else {
      idleSec = 0;
    }
  },
});

/* ── Réveil de l'attract mode ─────────────────────────────────────────────
   Tout geste réinitialise le compte d'inactivité ; un geste FRANC (touche ou
   clic) quitte en plus la démo. Le simple mouvement de souris réveille sans
   quitter — un tremblement de souris au repos ne doit pas tuer la démonstration,
   là où une frappe ou un clic sont, eux, une intention claire. */
window.addEventListener('pointermove', () => {
  idleSec = 0;
});
window.addEventListener('pointerdown', () => {
  idleSec = 0;
  stopAttract();
});

/* ── Pause clavier + auto-pause ──────────────────────────────────────── */
window.addEventListener('keydown', (e) => {
  // Toute frappe réveille : elle réarme le compte d'inactivité et, si la démo
  // tourne, la quitte sans rien faire d'autre — la même touche ne doit pas à la
  // fois sortir de l'attract mode ET déclencher une pause.
  idleSec = 0;
  if (app.state === App.Attract) {
    stopAttract();
    return;
  }
  // Masquer/afficher la bulle de debug — un simple bascule d'affichage, sans
  // toucher à `debugPanel` qui continue de calculer (le coût est négligeable) et
  // reprend là où il en était quand on la rouvre.
  if (e.key === '`' || e.code === 'Backquote') {
    if (dbg) dbg.style.display = dbg.style.display === 'none' ? '' : 'none';
    return;
  }
  if (e.key !== 'Escape') return;
  if (app.state === App.Play) app.to(App.Pause);
  else if (app.state === App.Pause) app.to(App.Play);
  else return;
  renderUi();
});

// ⚠️ Auto-pause obligatoire en navigateur (PLAN.md §6.12). Sans elle, un joueur
// qui change d'onglet revient devant un game over qu'il n'a pas vu venir.
document.addEventListener('visibilitychange', () => {
  if (document.hidden && app.state === App.Play) {
    app.to(App.Pause);
    renderUi();
  } else if (!document.hidden) {
    loop.resync();
  }
});

/* ── Panneau de mesure (développement) ───────────────────────────────── */
const dbg = document.getElementById('stats');
let dbgTick = 0;
function debugPanel(): void {
  if (!dbg || dbgTick++ % 12 !== 0) return;
  const info = stage.renderer.info;
  const w99 = workStats.percentile(0.99);
  const ok = w99 < 14 && workStats.filledEnough;
  dbg.innerHTML =
    `<span class="dim">— DEBUG — (\` masquer)</span>\n` +
    `<span class="dim">état</span>    ${app.state}\n` +
    `<span class="dim">charge</span>  ${world.enemies.count} ennemis · ${world.eb.count} balles · ${world.pickups.count} items\n` +
    `<span class="dim">travail</span> <span class="${ok ? 'ok' : 'bad'}">p99 ${w99.toFixed(1)} ms</span> ` +
    `<span class="dim">(critère &lt; 14)</span> · scale ${vfx.resolutionScale.toFixed(2)}\n` +
    `<span class="dim">gpu</span>     ${info.render.calls} draw calls · ` +
    (warmup.leaked(stage.renderer) > 0
      ? `<span class="bad">+${warmup.leaked(stage.renderer)} shaders APRÈS WARM-UP</span>`
      : `<span class="ok">shaders OK</span>`) +
    // VRAM des planches d'explosion — mesurée et affichée (critère J5).
    `\n<span class="dim">vfx</span>     flipbook ${(explosions.flipbookVram / (1024 * 1024)).toFixed(1)} Mo VRAM\n` +
    `<span class="dim">audio</span>   ` +
    (audio.ready ? `<span class="ok">actif</span>` : `<span class="bad">MUET (${audio.state})</span>`);
}

/* ── Démarrage ───────────────────────────────────────────────────────── */
for (const inv of checkFeelInvariants()) {
  if (!inv.ok) console.error(`✗ ${inv.name} — ${inv.detail}`);
}

/* ═══════════════════════════════════════════════════════════════════════════
   Filet de démarrage.

   ⚠️ Une exception pendant le boot produit un ÉCRAN NOIR SANS MESSAGE : le
   canvas existe, la boucle n'a jamais démarré, et rien n'indique où chercher.
   C'est le pire mode de panne possible — il ressemble à un bug de rendu alors
   que c'est un bug de chargement, et on perd des heures du mauvais côté.

   Toute erreur est donc AFFICHÉE, pas seulement journalisée. La console d'un
   navigateur ne fait pas partie du jeu ; l'écran, si.
   ═══════════════════════════════════════════════════════════════════════════ */
function fatal(where: string, err: unknown): void {
  console.error(`[boot] ${where}`, err);
  const box = document.createElement('pre');
  box.style.cssText =
    'position:fixed;inset:0;z-index:9999;margin:0;padding:24px;overflow:auto;' +
    'background:#0a0206;color:#ff8fa3;font:13px ui-monospace,Consolas,monospace;white-space:pre-wrap';
  const e = err as Error;
  box.textContent =
    `ERREUR DE DÉMARRAGE — ${where}\n\n${e?.message ?? String(err)}\n\n${e?.stack ?? ''}`;
  document.body.appendChild(box);
}

window.addEventListener('error', (e) => fatal('exception non capturée', e.error ?? e.message));
window.addEventListener('unhandledrejection', (e) => fatal('promesse rejetée', e.reason));

/* ── Perte de contexte WebGL ──────────────────────────────────────────────
   Le mode de panne n°1 sur mobile, et le mobile fait partie de la v1 : onglet
   laissé en arrière-plan, pression mémoire, bascule de GPU. Sans traitement,
   le jeu ne revient jamais — écran noir définitif, aucune erreur en console.

   Le geste minimal est de figer la boucle. Rendre sur un contexte mort
   consomme la batterie pour dessiner du néant, et surtout fait AVANCER la
   simulation pendant que le joueur ne voit rien : il retrouverait un vaisseau
   mort d'une balle qu'il n'a jamais vue arriver. */
const glGuard = new ContextLossGuard(
  stage.renderer.domElement,
  stage.renderer.getContext() as WebGLRenderingContext | null,
);
glGuard.freeze(loop);

let glLostBanner: HTMLElement | null = null;

glGuard.onLost(() => {
  console.warn('[webgl] contexte perdu — boucle figée en attendant la restauration.');
  if (!glLostBanner) {
    glLostBanner = document.createElement('div');
    glLostBanner.className = 'gl-lost';
    glLostBanner.textContent = 'CONTEXTE GRAPHIQUE PERDU — RESTAURATION EN COURS…';
    document.body.appendChild(glLostBanner);
  }
  glLostBanner.style.display = 'flex';
});

glGuard.onRestored(() => {
  void (async () => {
    try {
      /* ⚠️ `vfx.setSize()` d'abord, et ce n'est pas un détail d'ordre : les
         render targets du composer sont perdus avec le contexte. Les oublier
         reproduit exactement la panne du J8 — viewport de taille nulle, écran
         3D noir, alors que le HUD, l'audio et les écrans React fonctionnent,
         ce qui rend le diagnostic très long. */
      syncVfxSize();
      stage.resize();

      // Le warm-up est REFAIT : les programmes compilés appartenaient au
      // contexte disparu. Sans lui, les shaders se recompileraient un par un
      // pendant la reprise, c'est-à-dire au pire moment.
      view.primeForWarmup();
      smoke.primeForWarmup();
      await warmup.compile(stage.renderer, stage.scene, stage.camera, [stage.overlay]);

      // Les mesures d'avant la perte décrivent une autre réalité, et la
      // reconstruction elle-même pollue la fenêtre d'observation.
      workStats.reset();
      paceStats.reset();

      if (glLostBanner) glLostBanner.style.display = 'none';

      /* On ne rend JAMAIS la main directement au jeu. Réinjecter le joueur au
         milieu d'un rideau après une seconde d'absence est une mort qu'il ne
         peut pas s'attribuer. S'il était en partie, il reprend sur l'écran de
         pause et décide lui-même du moment. */
      if (app.state === App.Play) {
        app.to(App.Pause);
        renderUi();
      }
      loop.start();
    } catch (err) {
      fatal('restauration du contexte WebGL', err);
    }
  })();
});

void (async () => {
  try {
  await saves.load();
  void requestPersistence();
  // Les réglages sont reportés dès que la sauvegarde est lue. Le volume audio
  // sera réappliqué au déblocage — mais la secousse et le flash, eux, n'ont
  // aucun geste utilisateur pour les rattraper : un joueur qui a coupé les
  // secousses doit retrouver un écran calme dès la première frame.
  applyOptions();

  // Warm-up : peupler, compiler, rendre une frame complète, PUIS figer la
  // référence. Un lot vide n'est pas rendu, donc pas compilé.
  view.primeForWarmup();
  await warmup.compile(stage.renderer, stage.scene, stage.camera, [stage.overlay]);
  vfx.render(1 / 60);
  console.log(`Warm-up : ${warmup.mark(stage.renderer)} programmes.`);

  app.to(App.Title);
  renderUi();
  loop.start();
  } catch (err) {
    fatal('séquence de démarrage', err);
  }
})();

/** Utilisé par le banc de charge : injecte des projectiles pour mesurer le
 *  pire cas. Conservé hors du chemin de jeu. */
export function benchTopUp(target: number): void {
  const eb = world.eb;
  let miss = target - eb.count;
  if (miss > 48) miss = 48;
  for (let i = 0; i < miss; i++) {
    const a = world.rngVisual.next() * TAU;
    const sp = 260 + world.rngVisual.next() * 190;
    eb.spawn(
      world.rngVisual.range(-PF_HW, PF_HW),
      world.rngVisual.range(-PF_H * 0.2, PF_H * 0.5),
      cos(a) * sp,
      sin(a) * sp,
      11,
      1,
      world.rngVisual.int(3),
    );
  }
}
